import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { apiClient } from '../config/api';
import { showSuccess, showError } from '../utils/toast';
import './Users.css';
import './ConfirmModal.css';

// Review-and-approve panel. Automatic deletion is disabled in the backend; an Avatar
// is removed only when an admin confirms it here, and the backend refuses any Avatar
// whose Family Group is not yet 60+ days past its last paid subscription.
const DueForDeletion = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, avatar: null, ownerId: null, typedName: '', error: '', busy: false });

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await apiClient.get('/admin/deletion-review', token);
      const list = response?.data?.groups ?? [];
      setGroups(Array.isArray(list) ? list : []);
    } catch (err) {
      showError('Failed to load deletion review list: ' + (err.message || 'Unknown error'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '—';
    return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const ownerIdOf = (g) => {
    const owner = (g.members || []).find(m => m.is_profile_owner);
    return owner ? owner.user_id : null;
  };

  const openDeleteModal = (g, av) => {
    setDeleteModal({ open: true, avatar: av, ownerId: ownerIdOf(g), typedName: '', error: '', busy: false });
  };

  const closeDeleteModal = () => setDeleteModal({ open: false, avatar: null, ownerId: null, typedName: '', error: '', busy: false });

  const handleDeleteConfirm = async () => {
    const { avatar, ownerId, typedName } = deleteModal;
    const expectedName = (avatar.avatar_name || '').trim();
    if (!ownerId) {
      setDeleteModal(prev => ({ ...prev, error: 'Could not determine the Avatar owner for this group.' }));
      return;
    }
    if (typedName.trim() !== expectedName) {
      setDeleteModal(prev => ({ ...prev, error: `Type "${expectedName}" exactly to confirm.` }));
      return;
    }
    try {
      setDeleteModal(prev => ({ ...prev, busy: true, error: '' }));
      const token = localStorage.getItem('token');
      await apiClient.delete(`/admin/users/${ownerId}/avatars/${avatar.avatar_id}`, token);
      closeDeleteModal();
      showSuccess(`Avatar "${expectedName}" deleted.`);
      fetchGroups();
    } catch (err) {
      setDeleteModal(prev => ({ ...prev, busy: false, error: 'Delete failed: ' + (err.message || 'Unknown error') }));
    }
  };

  if (loading) {
    return <Layout><div className="users-loading">Loading deletion review list...</div></Layout>;
  }

  return (
    <Layout>
      <div className="users-page">
        <div className="users-page-header">
          <div className="header-left">
            <h1 className="page-title">Due for Deletion</h1>
          </div>
        </div>

        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px 2px', maxWidth: '760px' }}>
          Family Groups where no member has an active subscription and the most recent
          subscription ended 60+ days ago. Automatic deletion is disabled: nothing is
          deleted until an admin confirms it here. The Delete button only exists for
          Avatars on this list, and the backend refuses deletion for any Avatar that is
          not yet past the 60-day grace period.
        </p>

        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Avatar</th>
                <th>Group / Project</th>
                <th>Members</th>
                <th>Last Payment</th>
                <th>Subscription Ended</th>
                <th>Days Past 60-Day Grace</th>
                <th>Flags</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-users">
                    <div className="empty-state">No Family Groups are due for deletion.</div>
                  </td>
                </tr>
              ) : (
                groups.map((g, idx) => (
                  <tr key={`${(g.group_project_ids || []).join('-')}-${idx}`}>
                    <td className="row-number">{idx + 1}</td>
                    <td>
                      {(g.avatars || []).map(av => (
                        <div key={av.avatar_id} className="user-name-cell" style={{ marginBottom: '4px' }}>
                          <div className="user-avatar-small">{(av.avatar_name || 'A').charAt(0).toUpperCase()}</div>
                          <div className="user-name-info">
                            <div className="user-name">{av.avatar_name || 'Unnamed'}</div>
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>ID {av.avatar_id}</div>
                          </div>
                        </div>
                      ))}
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {(g.group_project_ids || []).length > 0
                        ? g.group_project_ids.map(id => `#${id}`).join(', ')
                        : <span style={{ color: '#6b7280' }}>No group (solo account)</span>}
                    </td>
                    <td>
                      {(g.members || []).map(mm => (
                        <div key={mm.user_id} style={{ fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 500 }}>{mm.first_name || '—'}</span>
                          <span style={{ color: '#6b7280' }}> · {mm.email}</span>
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}> (id {mm.user_id})</span>
                          {mm.is_profile_owner && (
                            <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: '9999px' }}>
                              Profile Owner
                            </span>
                          )}
                          {mm.is_admin && (
                            <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#b91c1c', background: '#fef2f2', padding: '1px 6px', borderRadius: '9999px' }}>
                              Admin
                            </span>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="date-cell">{formatDate(g.last_payment_date)}</td>
                    <td className="date-cell">{formatDate(g.last_subscription_end_date)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>
                      {g.days_past_60 != null ? `+${g.days_past_60}` : '—'}
                    </td>
                    <td>
                      {g.contains_admin ? (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#b91c1c' }}>
                          Contains admin account — hard-delete will refuse
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td>
                      {(g.avatars || []).map(av => (
                        <div key={av.avatar_id} style={{ marginBottom: '6px' }}>
                          <button
                            type="button"
                            className="action-btn"
                            title={`Permanently delete ${av.avatar_name || 'this Avatar'} and its data`}
                            onClick={() => openDeleteModal(g, av)}
                            style={{ color: '#dc2626', borderColor: '#fecaca', fontSize: '13px', padding: '4px 10px' }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete confirmation modal (same pattern as the Avatars page) */}
        {deleteModal.open && deleteModal.avatar && (
          <div className="confirm-modal-overlay" onClick={closeDeleteModal}>
            <div className="confirm-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div className="confirm-modal-header">
                <h3>Delete Avatar</h3>
              </div>
              <div className="confirm-modal-body">
                <p>
                  This will permanently delete <strong>{deleteModal.avatar.avatar_name || 'this Avatar'}</strong> (ID {deleteModal.avatar.avatar_id}) and all associated data. This cannot be undone.
                </p>
                <p style={{ marginTop: '12px', marginBottom: '6px', fontSize: '13px' }}>
                  Type <strong>{deleteModal.avatar.avatar_name}</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteModal.typedName}
                  onChange={e => setDeleteModal(prev => ({ ...prev, typedName: e.target.value, error: '' }))}
                  placeholder="Avatar name"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  autoFocus
                />
                {deleteModal.error && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{deleteModal.error}</p>
                )}
              </div>
              <div className="confirm-modal-actions">
                <button className="confirm-modal-btn confirm-modal-cancel" onClick={closeDeleteModal} disabled={deleteModal.busy}>
                  Cancel
                </button>
                <button className="confirm-modal-btn confirm-modal-confirm confirm-modal-danger" onClick={handleDeleteConfirm} disabled={deleteModal.busy}>
                  {deleteModal.busy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DueForDeletion;
